/**
 * Intelligent Caching Layer for Services
 * Implements multiple caching strategies with TTL and invalidation
 */

import { createLogger } from "@/utils/logger";

import { CacheOptions, CacheStrategy } from "./types";

const logger = createLogger("service-cache");

export interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  ttl: number;
  tags: string[];
  accessCount: number;
  lastAccessed: number;
}

export interface CacheStats {
  hits: number;
  misses: number;
  sets: number;
  deletes: number;
  evictions: number;
  size: number;
  hitRate: number;
}

/**
 * Enhanced cache implementation with intelligent eviction and statistics
 */
export class ServiceCache {
  private cache = new Map<string, CacheEntry>();
  private stats: CacheStats = {
    hits: 0,
    misses: 0,
    sets: 0,
    deletes: 0,
    evictions: 0,
    size: 0,
    hitRate: 0,
  };
  private maxSize: number;
  private defaultTtl: number;
  private cleanupInterval: NodeJS.Timeout;

  constructor(
    maxSize: number = 1000,
    defaultTtl: number = 5 * 60 * 1000, // 5 minutes
    cleanupIntervalMs: number = 60 * 1000, // 1 minute
  ) {
    this.maxSize = maxSize;
    this.defaultTtl = defaultTtl;

    // ⚡ OPTIMIZED: Incremental cleanup to prevent CPU blocking
    this.cleanupInterval = setInterval(() => {
      // In browser use requestIdleCallback when available; avoid window reference on server
      if (typeof window !== "undefined" &&
          "requestIdleCallback" in window) {
        // @ts-expect-error - type narrowing above guarantees presence in browser
        (window as any).requestIdleCallback(() => this.cleanup());
      } else {
        // Node.js or older browsers fallback
        setTimeout(() => this.cleanup(), 0);
      }
    }, cleanupIntervalMs);
  }

  /**
   * Get cached value with automatic expiration check
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      this.stats.misses++;
      this.updateHitRate();
      return null;
    }

    // Check if expired
    if (this.isExpired(entry)) {
      this.cache.delete(key);
      this.stats.misses++;
      this.stats.deletes++;
      this.updateStats();
      return null;
    }

    // Update access statistics
    entry.accessCount++;
    entry.lastAccessed = Date.now();
    this.stats.hits++;
    this.updateHitRate();

    logger.debug("Cache hit", {
      key,
      ttl: entry.ttl,
      accessCount: entry.accessCount,
    });
    return entry.data;
  }

  /**
   * Set cached value with options
   */
  set<T>(key: string, data: T, options: Partial<CacheOptions> = {}): void {
    const ttl = options.ttl || this.defaultTtl;
    const tags = options.tags || [];

    // Ensure we don't exceed max size
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      this.evictLRU();
    }

    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
      tags,
      accessCount: 0,
      lastAccessed: Date.now(),
    };

    this.cache.set(key, entry);
    this.stats.sets++;
    this.updateStats();

    logger.debug("Cache set", { key, ttl, tags, size: this.cache.size });
  }

  /**
   * Delete specific cache entry
   */
  delete(key: string): boolean {
    const deleted = this.cache.delete(key);
    if (deleted) {
      this.stats.deletes++;
      this.updateStats();
      logger.debug("Cache delete", { key });
    }
    return deleted;
  }

  /**
   * Clear all cache entries or by tag pattern
   */
  clear(pattern?: string): number {
    if (!pattern) {
      const size = this.cache.size;
      this.cache.clear();
      this.stats.deletes += size;
      this.updateStats();
      logger.info("Cache cleared completely", { deletedEntries: size });
      return size;
    }

    let deleted = 0;
    const regex = new RegExp(pattern);

    for (const [key, entry] of this.cache.entries()) {
      const matchesKey = regex.test(key);
      const matchesTags = entry.tags.some((tag) => regex.test(tag));

      if (matchesKey || matchesTags) {
        this.cache.delete(key);
        deleted++;
      }
    }

    this.stats.deletes += deleted;
    this.updateStats();
    logger.info("Cache cleared by pattern", {
      pattern,
      deletedEntries: deleted,
    });
    return deleted;
  }

  /**
   * Check if cache has valid entry for key
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    if (this.isExpired(entry)) {
      this.cache.delete(key);
      this.stats.deletes++;
      this.updateStats();
      return false;
    }

    return true;
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    return { ...this.stats };
  }

  /**
   * Get all cache keys (for debugging)
   */
  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Get cache size
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Cleanup expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    let expired = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (this.isExpired(entry, now)) {
        this.cache.delete(key);
        expired++;
      }
    }

    if (expired > 0) {
      this.stats.deletes += expired;
      this.updateStats();
      logger.debug("Cache cleanup completed", { expiredEntries: expired });
    }
  }

  /**
   * Evict least recently used entry
   */
  private evictLRU(): void {
    let oldestKey: string | null = null;
    let oldestTime = Date.now();

    for (const [key, entry] of this.cache.entries()) {
      if (entry.lastAccessed < oldestTime) {
        oldestTime = entry.lastAccessed;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.stats.evictions++;
      logger.debug("Cache LRU eviction", { evictedKey: oldestKey });
    }
  }

  /**
   * Check if cache entry is expired
   */
  private isExpired(entry: CacheEntry, now: number = Date.now()): boolean {
    return now - entry.timestamp > entry.ttl;
  }

  /**
   * Update cache statistics
   */
  private updateStats(): void {
    this.stats.size = this.cache.size;
    this.updateHitRate();
  }

  /**
   * Update hit rate calculation
   */
  private updateHitRate(): void {
    const total = this.stats.hits + this.stats.misses;
    this.stats.hitRate = total > 0 ? this.stats.hits / total : 0;
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.cache.clear();
  }
}

/**
 * Cache manager for handling multiple cache strategies
 */
export class CacheManager {
  private caches = new Map<string, ServiceCache>();
  private strategies = new Map<string, CacheStrategy>();

  /**
   * Register a cache strategy for a service
   */
  registerStrategy(serviceName: string, strategy: CacheStrategy): void {
    this.strategies.set(serviceName, strategy);

    if (strategy.enabled && !this.caches.has(serviceName)) {
      this.caches.set(
        serviceName,
        new ServiceCache(
          1000, // max size
          strategy.defaultTtl,
        ),
      );
    }

    logger.debug("Cache strategy registered", { serviceName, strategy });
  }

  /**
   * Get cache instance for service
   */
  getCache(serviceName: string): ServiceCache | null {
    const strategy = this.strategies.get(serviceName);
    if (!strategy?.enabled) return null;

    if (!this.caches.has(serviceName)) {
      this.caches.set(serviceName, new ServiceCache(1000, strategy.defaultTtl));
    }

    return this.caches.get(serviceName) || null;
  }

  /**
   * Execute caching strategy for a service operation
   */
  async executeWithCache<T>(
    serviceName: string,
    key: string,
    operation: () => Promise<T>,
    options: Partial<CacheOptions> = {},
  ): Promise<T> {
    const strategy = this.strategies.get(serviceName);
    const cache = this.getCache(serviceName);

    if (!strategy?.enabled || !cache) {
      return operation();
    }

    const cacheKey = `${serviceName}:${key}`;

    // Handle different cache strategies
    switch (strategy.strategy) {
      case "cache-first":
        return this.cacheFirst(cache, cacheKey, operation, options);
      case "network-first":
        return this.networkFirst(cache, cacheKey, operation, options);
      case "cache-only":
        return this.cacheOnly(cache, cacheKey);
      case "network-only":
        return operation();
      default:
        return this.cacheFirst(cache, cacheKey, operation, options);
    }
  }

  /**
   * Cache-first strategy: Check cache, fallback to network
   */
  private async cacheFirst<T>(
    cache: ServiceCache,
    key: string,
    operation: () => Promise<T>,
    options: Partial<CacheOptions>,
  ): Promise<T> {
    const cached = cache.get<T>(key);
    if (cached !== null) {
      return cached;
    }

    const result = await operation();
    cache.set(key, result, options);
    return result;
  }

  /**
   * Network-first strategy: Try network, fallback to cache
   */
  private async networkFirst<T>(
    cache: ServiceCache,
    key: string,
    operation: () => Promise<T>,
    options: Partial<CacheOptions>,
  ): Promise<T> {
    try {
      const result = await operation();
      cache.set(key, result, options);
      return result;
    } catch (error) {
      const cached = cache.get<T>(key);
      if (cached !== null) {
        logger.warn("Network failed, returning cached data", { key, error });
        return cached;
      }
      throw error;
    }
  }

  /**
   * Cache-only strategy: Only return cached data
   */
  private async cacheOnly<T>(cache: ServiceCache, key: string): Promise<T> {
    const cached = cache.get<T>(key);
    if (cached === null) {
      throw new Error(`No cached data available for key: ${key}`);
    }
    return cached;
  }

  /**
   * Clear cache for service or pattern
   */
  clearCache(serviceName: string, pattern?: string): number {
    const cache = this.caches.get(serviceName);
    if (!cache) return 0;

    return cache.clear(pattern);
  }

  /**
   * Get cache statistics for all services
   */
  getAllStats(): Record<string, CacheStats> {
    const stats: Record<string, CacheStats> = {};

    for (const [serviceName, cache] of this.caches.entries()) {
      stats[serviceName] = cache.getStats();
    }

    return stats;
  }

  /**
   * Cleanup all caches
   */
  destroy(): void {
    for (const cache of this.caches.values()) {
      cache.destroy();
    }
    this.caches.clear();
    this.strategies.clear();
  }
}

// Global cache manager instance
export const cacheManager = new CacheManager();
